import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { AppVersionService } from '@app/services/app-version.service';
import { MainPageActions } from '@shared/enums/main-page-actions';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { MenuSectionItem } from '@shared/interfaces/menu-config.interface';
import { selectUserPermissionLevel } from '@shared/selectors/user.selectors';
import { HelpRouterService } from '@shared/services/help-router.service';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { ModalsService } from '@ui-components/modals/modals.service';
import { NewTurnoverModalComponent } from '@ui-components/modals/new-turnover-modal/new-turnover-modal.component';

@UntilDestroy()
@Component({
  selector: 'app-side-panel-section-item',
  templateUrl: './side-panel-section-item.component.html',
  styleUrls: ['./side-panel-section-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidePanelSectionItemComponent extends ComponentAbstract implements OnInit, OnDestroy {
  @Input() sectionItem: MenuSectionItem;
  @Output() menuActionsEvent: EventEmitter<MainPageActions> = new EventEmitter<MainPageActions>();

  currentVersion = '';
  needUpgrade = false;
  activeSection = null;

  permissionLevels$ = this.store.select(selectUserPermissionLevel).pipe(
    untilDestroyed(this),
    filter((permissionLevel: PermissionLevelType[]) => !!permissionLevel?.length)
  );

  constructor(
    protected cdr: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
    private modalsService: ModalsService,
    private appVersionService: AppVersionService,
    private helpRouterService: HelpRouterService,
    private store: Store,
    private snackbarService: SnackbarService
  ) {
    super(cdr);
  }

  ngOnInit(): void {
    this.isActive = window.location.pathname.startsWith(this.sectionItem.urlPath);
    this.activeSection = this.sectionItem.sectionItem.find(item => window.location.pathname.startsWith(item.urlPath));
    this.appVersionService.version$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(appVersion => {
          if (this.currentVersion && this.currentVersion !== appVersion.version_number) {
            this.needUpgrade = true;
          }
          this.currentVersion = appVersion.version_number;
        })
      )
      .subscribe();

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationEnd),
        distinctUntilChanged(),
        tap((event: NavigationEnd) => {
          this.isActive = event.url.startsWith(this.sectionItem.urlPath);
          this.activeSection = this.sectionItem.sectionItem.find(item => event.url.startsWith(item.urlPath));
          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {}

  async open(menuSectionItem: MenuSectionItem) {
    if (menuSectionItem.permissionLevelWarning) {
      const permissionLevels = await firstValueFrom(this.permissionLevels$);
      if (!checkPermissionLevel(permissionLevels, menuSectionItem.permissionLevelWarning)) {
        this.snackbarService.warning(`Permission: ${menuSectionItem.permissionLevelWarning.toLowerCase()} required`);
        return;
      }
    }

    if (menuSectionItem.modalDialogComponent) {
      switch (menuSectionItem.modalDialogComponent) {
        case NewTurnoverModalComponent:
          this.modalsService.openNewTurnoverModal([], false);
          break;
        default:
          this.dialog.open(menuSectionItem.modalDialogComponent);
      }

      return;
    }

    if (menuSectionItem.urlPath) {
      const queryParams = this.helpRouterService.getGlobalPreservedParams();

      if (menuSectionItem.newTab) {
        const url = this.router.serializeUrl(this.router.createUrlTree([menuSectionItem.urlPath], { queryParams }));

        window.open(url, '_blank');
        return;
      }

      if (this.needUpgrade) {
        const url = this.router.createUrlTree([menuSectionItem.urlPath], { queryParams });
        window.location.href = url.toString();
      } else {
        this.router.navigate([menuSectionItem.urlPath], { queryParams }).then();
      }
    }

    if (menuSectionItem.menuActions) {
      this.menuActionsEvent.emit(menuSectionItem.menuActions);
    }
  }

  toggleSection($event: MouseEvent) {
    $event.stopPropagation();
    this.isExpanded = !this.isExpanded;
    this.cdr.detectChanges();
  }
}
